import * as React from "react";
import { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import Paper from "@mui/material/Paper";
import {
  Edit as EditIcon,
  Add as AddIcon,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  Button,
  TextField,
  Box,
  CircularProgress,
  Typography,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import { message, Modal } from "antd";

const API_BASE_URL = "https://uktomars.co.uk/api";

const GetPromotions = () => {
  const [searchText, setSearchText] = useState("");
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 100,
  });

  useEffect(() => {
    getApiPromotions();
  }, []);

  const getApiPromotions = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/admin/promotions`);
      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const responseData = await res.json();
      const promotions = Array.isArray(responseData?.data)
        ? responseData.data
        : Array.isArray(responseData)
          ? responseData
          : [];

      setDataSource(
        promotions.map((item, index) => ({
          index: index + 1,
          id: item.id,
          title: item.title || "",
          image_url: item.image_url || "",
          pdf_url: item.pdf_url || "",
          pdf_file_url: item.pdf_file_url || "",

          is_active: !!item.is_active,
        })),
      );
    } catch (error) {
      console.error("Error fetching promotions:", error);
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePromotion = (id) => {
    Modal.confirm({
      title: "Remove Promotion",
      content: "Are you sure you want to remove this promotion?",
      okText: "Remove",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      async onOk() {
        try {
          const res = await fetch(`${API_BASE_URL}/admin/promotions/${id}`, {
            method: "DELETE",
          });

          if (!res.ok) {
            throw new Error(`HTTP error! Status: ${res.status}`);
          }

          setDataSource((prev) =>
            prev
              .filter((item) => item.id !== id)
              .map((item, index) => ({ ...item, index: index + 1 })),
          );
          message.success("Promotion removed successfully");
        } catch (error) {
          console.error("Error removing promotion:", error);
          message.error("Failed to remove promotion");
        }
      },
    });
  };

  const columns = [
    { field: "index", headerName: "#", width: 70 },
    { field: "title", headerName: "Title", width: 220 },
    {
      field: "image_url",
      headerName: "Image",
      width: 130,
      renderCell: (params) =>
        params.row.image_url ? (
          <Box
            component="img"
            src={params.row.image_url}
            alt={params.row.title}
            sx={{ width: 48, height: 48, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No image
          </Typography>
        ),
    },
    {
      field: "pdf_file_url",
      headerName: "PDF",
      width: 160,
      renderCell: (params) => {
        const href = params.row.pdf_file_url || params.row.pdf_url || "";
        if (!href) {
          return (
            <Typography variant="caption" color="text.secondary">
              No PDF
            </Typography>
          );
        }

        return (
          <a href={href} target="_blank" rel="noreferrer">
            <Button size="small" variant="outlined">
              View
            </Button>
          </a>
        );
      },
    },

    {
      field: "is_active",
      headerName: "Status",
      width: 130,
      renderCell: (params) =>
        params.row.is_active ? (
          <Typography sx={{ color: "#3A6F43", fontWeight: 700 }}>
            Active
          </Typography>
        ) : (
          <Typography sx={{ color: "#d32f2f", fontWeight: 700 }}>
            Inactive
          </Typography>
        ),
    },
    {
      field: "action",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Link to={`/admin/promotions/edit/${params.row.id}`}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                backgroundColor: "#3A6F43",
                color: "#fff",
                minWidth: 40,
                px: 1.5,
                "&:hover": { backgroundColor: "#3A6F43" },
              }}
            />
          </Link>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => handleRemovePromotion(params.row.id)}
          />
        </Box>
      ),
    },
  ];

  const filteredRows = dataSource.filter((row) =>
    Object.values(row).some((val) =>
      String(val).toLowerCase().includes(searchText.toLowerCase()),
    ),
  );

  const start = paginationModel.page * paginationModel.pageSize;
  const end = start + paginationModel.pageSize;
  const pagedRows = filteredRows.slice(start, end);

  return (
    <div style={{ margin: "25px", overflow: "hidden" }}>
      <Box
        sx={{
          padding: "16px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          backgroundColor: "#fff",
          borderRadius: "8px",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          mb: 2,
        }}
      >
        <TextField
          placeholder="Search Promotions"
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 250,
            maxWidth: 400,
            backgroundColor: "#f5f5f5",
          }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />

        <Link to="/admin/promotions/add" style={{ textDecoration: "none" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: "#3f51b5",
              color: "#fff",
              "&:hover": { backgroundColor: "#303f9f" },
              borderRadius: "6px",
              padding: "10px 20px",
              whiteSpace: "nowrap",
            }}
          >
            Add Promotion
          </Button>
        </Link>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
          }}
        >
          <CircularProgress size={60} sx={{ color: "#3f51b5" }} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading Promotions...
          </Typography>
        </Box>
      ) : (
        <Paper
          sx={{
            width: "100%",
            mb: 10,
            p: 2,
            overflowX: "auto",
            borderRadius: "8px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="body2" sx={{ mr: 2 }}>
                Rows per page:
              </Typography>
              <Select
                value={paginationModel.pageSize}
                onChange={(e) =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    pageSize: e.target.value,
                    page: 0,
                  }))
                }
                size="small"
                sx={{ height: "36px", minWidth: "80px" }}
              >
                {[10, 20, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Typography variant="body2">
              {`${start + 1}-${Math.min(end, filteredRows.length)} of ${
                filteredRows.length
              }`}
            </Typography>

            <Box>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: Math.max(prev.page - 1, 0),
                  }))
                }
                disabled={paginationModel.page === 0}
                size="small"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() =>
                  setPaginationModel((prev) => ({
                    ...prev,
                    page: prev.page + 1,
                  }))
                }
                disabled={
                  (paginationModel.page + 1) * paginationModel.pageSize >=
                  filteredRows.length
                }
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          <DataGrid
            rows={pagedRows}
            columns={columns}
            autoHeight
            disableSelectionOnClick
            hideFooter
            sx={{
              minWidth: 950,
              border: 0,
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #e0e0e0",
              },
            }}
          />
        </Paper>
      )}
    </div>
  );
};

export default GetPromotions;
